import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  GATE_CONFIG, DECISION_CONFIG, PARENT_STATUS_MAP,
  getInitials, getAvatarColor, fmt,
} from "./RequestCardUtils";
import { getAllocationRequestById } from "../../../../../api/approvals";

export const FTEBar = ({ fte, t }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <Box sx={{
      width: "80px", height: "4px",
      background: t.surface3, borderRadius: "2px", overflow: "hidden",
    }}>
      <Box sx={{
        height: "100%",
        width: `${Math.min((fte || 0) * 100, 100)}%`,
        background: t.accent, borderRadius: "2px",
      }} />
    </Box>
    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text2 }}>
      {fte} FTE
    </Typography>
  </Box>
);

export const ParentRequestBlock = ({ parentRequestId, parentRequestCode, parentRequestStatus, t, isDark }) => {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!parentRequestId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    getAllocationRequestById(parentRequestId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => { if (!cancelled) setError("Failed to load original request."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [parentRequestId]);

  const ps = PARENT_STATUS_MAP[parentRequestStatus] ?? { label: parentRequestStatus, color: t.text3 };

  return (
    <Box sx={{
      mt: "10px",
      border: "1px solid rgba(214,223,39,0.25)",
      borderRadius: "4px",
      overflow: "hidden",
    }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: "8px",
        padding: "7px 12px",
        background: isDark ? "rgba(214,223,39,0.06)" : "rgba(214,223,39,0.07)",
        borderBottom: "1px solid rgba(214,223,39,0.18)",
      }}>
        <Typography sx={{
          fontFamily: "'DM Mono', monospace", fontSize: "8px",
          letterSpacing: "0.16em", textTransform: "uppercase",
          color: "#6B7300", fontWeight: 700,
        }}>
          ↺ Original Request
        </Typography>
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.accent, opacity: 0.85 }}>
          {parentRequestCode}
        </Typography>
        <Box sx={{
          ml: "auto", padding: "1px 8px", borderRadius: "2px",
          background: `${ps.color}18`, border: `1px solid ${ps.color}44`,
        }}>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: ps.color, letterSpacing: "0.06em" }}>
            {ps.label}
          </Typography>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 12px" }}>
          <CircularProgress size={12} sx={{ color: t.accent }} />
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>Loading…</Typography>
        </Box>
      )}

      {!loading && error && (
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: "#c84040", padding: "10px 12px" }}>
          {error}
        </Typography>
      )}

      {!loading && !error && detail && (
        <Box sx={{ padding: "10px 12px" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", mb: "8px" }}>
            {[
              { label: "FTE",   value: detail.fte != null ? `${detail.fte} FTE` : "—" },
              { label: "Start", value: fmt(detail.startDate) },
              { label: "End",   value: fmt(detail.endDate)   },
            ].map(({ label, value }) => (
              <Box key={label}>
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace", fontSize: "7px",
                  letterSpacing: "0.12em", textTransform: "uppercase",
                  color: t.text3, mb: "2px",
                }}>
                  {label}
                </Typography>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text2 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {detail.rejectionReason && (
            <Box sx={{
              padding: "7px 10px",
              background: "rgba(200,64,64,0.07)",
              border: "1px solid rgba(200,64,64,0.22)",
              borderRadius: "3px",
            }}>
              <Typography sx={{
                fontFamily: "'DM Mono', monospace", fontSize: "7px",
                letterSpacing: "0.12em", textTransform: "uppercase",
                color: "#c84040", mb: "3px",
              }}>
                Rejection Reason
              </Typography>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: t.text2, lineHeight: 1.5 }}>
                {detail.rejectionReason}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export const HistoryPanel = ({ request, t }) => {
  const approvals = request.approvals || [];

  return (
    <Box sx={{ padding: "14px 16px 16px", borderTop: `1px solid ${t.border}`, background: t.surface2 }}>

      <Box sx={{ mb: approvals.length > 0 ? "14px" : 0 }}>
        <Typography sx={{
          fontFamily: "'DM Mono', monospace", fontSize: "8px",
          letterSpacing: "0.14em", textTransform: "uppercase",
          color: t.text3, mb: "6px",
        }}>
          Submission
        </Typography>
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 12px", borderRadius: "4px",
          border: `1px solid ${t.border}`, background: t.surface,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box sx={{
              width: "24px", height: "24px", borderRadius: "50%", flexShrink: 0,
              background: `${getAvatarColor(request.submittedBy)}22`,
              border: `1px solid ${getAvatarColor(request.submittedBy)}55`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'DM Mono', monospace", fontSize: "8px",
              fontWeight: 700, color: getAvatarColor(request.submittedBy),
            }}>
              {getInitials(request.submittedBy)}
            </Box>
            <Box>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", fontWeight: 500, color: t.text }}>
                {request.submittedBy || "—"}
              </Typography>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
                {request.submittedByMac}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
            {request.submittedAgo}
          </Typography>
        </Box>
      </Box>

      {approvals.length > 0 && (
        <Box>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace", fontSize: "8px",
            letterSpacing: "0.14em", textTransform: "uppercase",
            color: t.text3, mb: "6px",
          }}>
            Approval Trail
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {approvals.map((approval, i) => {
              const gateCfg     = GATE_CONFIG[approval.gate]         || { label: approval.gate, color: t.accent };
              const decisionCfg = DECISION_CONFIG[approval.decision] || DECISION_CONFIG.PENDING;
              const avatarColor = getAvatarColor(approval.decidedByName);

              return (
                <Box key={i} sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 12px", borderRadius: "4px",
                  border: `1px solid ${t.border}`, background: t.surface,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Box sx={{
                      padding: "2px 8px", borderRadius: "3px",
                      background: `${gateCfg.color}14`, border: `1px solid ${gateCfg.color}33`, flexShrink: 0,
                    }}>
                      <Typography sx={{
                        fontFamily: "'DM Mono', monospace", fontSize: "8px",
                        fontWeight: 700, color: gateCfg.color, letterSpacing: "0.1em",
                      }}>
                        {gateCfg.label}
                      </Typography>
                    </Box>
                    <Box sx={{
                      width: "24px", height: "24px", borderRadius: "50%", flexShrink: 0,
                      background: `${avatarColor}22`, border: `1px solid ${avatarColor}55`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'DM Mono', monospace", fontSize: "8px",
                      fontWeight: 700, color: avatarColor,
                    }}>
                      {getInitials(approval.decidedByName)}
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", fontWeight: 500, color: t.text }}>
                        {approval.decidedByName || "—"}
                      </Typography>
                      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
                        {approval.decidedAtFmt}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "3px" }}>
                    <Box sx={{
                      padding: "2px 8px", borderRadius: "3px",
                      background: decisionCfg.bg, border: `1px solid ${decisionCfg.border}`,
                    }}>
                      <Typography sx={{
                        fontFamily: "'DM Mono', monospace", fontSize: "8px",
                        fontWeight: 700, color: decisionCfg.color, letterSpacing: "0.08em",
                      }}>
                        {decisionCfg.label}
                      </Typography>
                    </Box>
                    {approval.comments && (
                      <Typography sx={{
                        fontFamily: "'Inter', sans-serif", fontSize: "10px", color: t.text3,
                        maxWidth: "200px", textAlign: "right",
                      }}>
                        "{approval.comments}"
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {approvals.length === 0 && (
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3, mt: "4px" }}>
          No approvals recorded yet
        </Typography>
      )}
    </Box>
  );
};